import { useEffect, useRef, useState } from 'react';

/**
 * Live input level for a MediaStream, for the recorder's meter.
 *
 * Reads an AnalyserNode ~20 times a second and reduces it to what a person
 * needs while a meeting is running: how loud it is right now, whether that
 * counts as speech, and how long it has been since anything did. The
 * classification lives in `classifyLevel` so it can be tested without a
 * browser.
 */

export interface AudioLevel {
  /** 0..1, RMS with a short release so the bar does not flicker. */
  level: number;
  /** Instantaneous RMS in dBFS; -100 when nothing is coming in. */
  db: number;
  /** Highest level seen in the last second, 0..1. */
  peak: number;
  /** Speech-like energy right now (hysteresis so a pause mid-sentence stays "speaking"). */
  speaking: boolean;
  /** Seconds since the last speech-like moment; 0 while speaking. */
  silentForSeconds: number;
  /** The analyser is attached and producing readings. */
  active: boolean;
}

export const IDLE_LEVEL: AudioLevel = {
  level: 0, db: -100, peak: 0, speaking: false, silentForSeconds: 0, active: false,
};

/** Above this the input is speech-like; below it (minus hysteresis) it is not. */
export const SPEECH_DB = -42;
const SPEECH_RELEASE_DB = 6;
/** Below this the mic is effectively not hearing the room. */
export const QUIET_DB = -55;

export type LevelBand = 'silent' | 'quiet' | 'good' | 'loud' | 'clipping';

/** What the bar's colour and the hint text are chosen from. */
export function classifyLevel(db: number, peak: number): LevelBand {
  if (peak >= 0.98) return 'clipping';
  if (db < QUIET_DB) return 'silent';
  if (db < SPEECH_DB) return 'quiet';
  if (db > -12) return 'loud';
  return 'good';
}

/** RMS of a time-domain sample block, as dBFS. */
export function rmsDb(samples: Float32Array): number {
  if (samples.length === 0) return -100;
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) sum += samples[i] * samples[i];
  const rms = Math.sqrt(sum / samples.length);
  return rms > 0 ? Math.max(-100, 20 * Math.log10(rms)) : -100;
}

/** Pure step of the speech/silence state machine, so it can be unit-tested. */
export function nextSpeaking(previous: boolean, db: number): boolean {
  return previous ? db > SPEECH_DB - SPEECH_RELEASE_DB : db > SPEECH_DB;
}

type AudioContextCtor = typeof AudioContext;

function audioContextConstructor(): AudioContextCtor | null {
  const w = window as Window & { webkitAudioContext?: AudioContextCtor };
  return window.AudioContext || w.webkitAudioContext || null;
}

export function useAudioLevel(stream: MediaStream | null, enabled = true): AudioLevel {
  const [reading, setReading] = useState<AudioLevel>(IDLE_LEVEL);
  const speakingRef = useRef(false);
  const lastSpeechAtRef = useRef<number>(0);
  const smoothedRef = useRef(0);
  const peakRef = useRef<{ value: number; at: number }>({ value: 0, at: 0 });

  useEffect(() => {
    if (!stream || !enabled || stream.getAudioTracks().length === 0) {
      setReading(IDLE_LEVEL);
      return undefined;
    }
    const Ctor = audioContextConstructor();
    // No analyser (an old WebKit, or a test double) means no meter — never a crash.
    if (!Ctor || typeof Ctor.prototype?.createAnalyser !== 'function') return undefined;

    let context: AudioContext;
    try {
      context = new Ctor();
    } catch {
      return undefined;
    }
    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0;
    const source = context.createMediaStreamSource(stream);
    source.connect(analyser);
    const buffer = new Float32Array(analyser.fftSize);

    let raf = 0;
    let lastTick = 0;
    lastSpeechAtRef.current = performance.now();
    speakingRef.current = false;
    smoothedRef.current = 0;

    const tick = (now: number) => {
      raf = window.requestAnimationFrame(tick);
      if (now - lastTick < 50) return; // ~20 Hz is plenty for a meter
      lastTick = now;
      analyser.getFloatTimeDomainData(buffer);
      const db = rmsDb(buffer);
      const linear = Math.min(1, Math.max(0, (db + 60) / 60)); // -60 dB .. 0 dB → 0..1
      // Fast attack, slow release: the bar jumps up with speech and eases down.
      smoothedRef.current = linear > smoothedRef.current ? linear : smoothedRef.current * 0.85;
      if (linear >= peakRef.current.value || now - peakRef.current.at > 1000) {
        peakRef.current = { value: linear, at: now };
      }
      const speaking = nextSpeaking(speakingRef.current, db);
      speakingRef.current = speaking;
      if (speaking) lastSpeechAtRef.current = now;
      setReading({
        level: smoothedRef.current,
        db,
        peak: peakRef.current.value,
        speaking,
        silentForSeconds: speaking ? 0 : Math.floor((now - lastSpeechAtRef.current) / 1000),
        active: true,
      });
    };
    raf = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(raf);
      try { source.disconnect(); } catch { /* already gone */ }
      void context.close().catch(() => undefined);
      setReading(IDLE_LEVEL);
    };
  }, [stream, enabled]);

  return reading;
}
